import React from 'react';
import LegalLayout from '../components/LegalLayout';

const DracoGermanTerms: React.FC = () => {
  return (
    <LegalLayout title="DracoGerman Terms of Use" lastUpdated="October 7, 2026">
      <p>
        These terms apply to your use of DracoGerman, an app for learning German made by
        Spiegelei Studio (see the Impressum in the footer below for our company details). By
        downloading or using the app, you agree to these terms. If you do not agree, please do
        not use the app.
      </p>

      <h2>1. The app</h2>
      <p>
        DracoGerman offers German vocabulary, flashcards, podcasts, and training exercises, as
        well as two AI-supported features: pronunciation scoring and AI analysis in Live
        Listening. The learning content is free to use and does not require an account.
      </p>
      <p>
        We grant you a personal, non-exclusive, non-transferable licence to use the app on
        devices you own or control, for your own non-commercial learning.
      </p>

      <h2>2. Accounts</h2>
      <p>
        Pronunciation scoring, AI analysis, and Premium require an account. You must provide a
        valid email address or sign in with Apple or Google, keep your sign-in details
        confidential, and not share your account with others. If you are under 16, you may only
        create an account with the permission of a parent or guardian.
      </p>
      <p>
        You can delete your account at any time in the app under Account &gt; Delete account.
      </p>

      <h2>3. Premium subscription</h2>
      <p>
        DracoGerman Premium is an optional, automatically renewing subscription that raises the
        daily limits for pronunciation scoring and AI analysis. The subscription periods on
        offer, their prices, and the limits that currently apply are shown in the app before
        you subscribe.
      </p>
      <ul>
        <li>
          <strong>Payment.</strong> Subscriptions are sold through the Apple App Store and
          charged to your Apple Account when you confirm the purchase.
        </li>
        <li>
          <strong>Renewal.</strong> The subscription renews automatically for the same period
          unless you cancel at least 24 hours before the end of the current period. Your
          account is charged for the renewal within 24 hours before the period ends.
        </li>
        <li>
          <strong>Cancelling.</strong> You can cancel at any time in your App Store account
          settings. Premium stays active until the end of the period you have paid for.
          Deleting the app or your DracoGerman account does not cancel the subscription.
        </li>
        <li>
          <strong>Free trial.</strong> If a free trial is offered, it turns into a paid
          subscription when the trial ends unless you cancel before then. A trial is available
          once per Apple Account.
        </li>
        <li>
          <strong>Price changes.</strong> If the price of your subscription changes, you will
          be informed in advance through the App Store and can cancel before the new price
          applies.
        </li>
        <li>
          <strong>Refunds and withdrawal.</strong> Purchases are processed by Apple under
          Apple's terms. Requests for refunds, and the exercise of any statutory right of
          withdrawal, are handled by Apple, for example at{' '}
          <a href="https://reportaproblem.apple.com">reportaproblem.apple.com</a>.
        </li>
      </ul>

      <h2>4. Usage limits</h2>
      <p>
        Pronunciation scoring and AI analysis are limited to a number of uses per day, on both
        the free plan and Premium. Each use causes costs for us with third-party providers, and
        the limits keep the service affordable and protect it from abuse. The limits are shown
        in the app. Unused requests do not carry over to the next day.
      </p>
      <p>
        We may adjust the limits. We will not reduce the limits of a Premium subscription
        during a period you have already paid for.
      </p>

      <h2>5. AI features</h2>
      <p>
        Pronunciation scores, explanations, translations, and summaries are generated
        automatically. They are learning aids and may be incomplete or wrong. The transcript in
        Live Listening comes from automatic speech recognition and may contain errors. Do not
        rely on these results where accuracy matters, for example for exams, official
        documents, or medical, legal, or financial decisions.
      </p>

      <h2>6. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          use Live Listening to transcribe private conversations, meetings, or other audio of
          people who have not agreed to it, or to send confidential or personal information
          about others for AI analysis. Live Listening is intended for publicly available audio
          such as podcasts, news, and videos;
        </li>
        <li>use the app in a way that breaks the law or infringes the rights of others;</li>
        <li>
          access our servers other than through the app, automate requests, or try to get
          around usage limits or technical protections;
        </li>
        <li>
          copy, resell, or redistribute the app's content, or decompile or reverse engineer the
          app, except where the law expressly allows it.
        </li>
      </ul>
      <p>
        If you seriously or repeatedly break these rules, we may suspend or close your account.
      </p>

      <h2>7. Content and intellectual property</h2>
      <p>
        The app and its content, including word lists, example sentences, translations, audio,
        and podcasts, belong to Spiegelei Studio or its licensors and are protected by
        copyright. Apart from the licence in section 1, no rights are transferred to you.
      </p>

      <h2>8. Availability and changes to the app</h2>
      <p>
        We work to keep the app available and its content correct, but we do not guarantee
        uninterrupted availability. Features that depend on third-party services may be
        temporarily unavailable. We may change, add, or remove features and content. If we
        permanently discontinue a paid feature, your statutory rights remain unaffected.
      </p>

      <h2>9. Liability</h2>
      <p>
        We are liable without limitation for intent and gross negligence, for injury to life,
        body, or health, and under mandatory statutory provisions such as the German Product
        Liability Act.
      </p>
      <p>
        In cases of slight negligence, we are liable only for breaches of essential contractual
        obligations, meaning obligations whose fulfilment makes the proper performance of the
        contract possible in the first place and on which you may regularly rely. In these
        cases our liability is limited to the foreseeable damage typical for this kind of
        contract. Any further liability is excluded.
      </p>

      <h2>10. Privacy</h2>
      <p>
        How we handle your data is described in our{' '}
        <a href="/dracogerman/privacy">Privacy Policy</a>.
      </p>

      <h2>11. Changes to these terms</h2>
      <p>
        We may update these terms, for example when features or legal requirements change. We
        will revise the "last updated" date above and, for significant changes, inform you in
        the app. Changes do not apply retroactively to a subscription period you have already
        paid for.
      </p>

      <h2>12. Governing law</h2>
      <p>
        These terms are governed by the law of the Federal Republic of Germany. If you are a
        consumer, the mandatory consumer protection provisions of the country in which you
        normally live continue to apply. We are neither obliged nor willing to take part in
        dispute resolution proceedings before a consumer arbitration board.
      </p>

      <h2>13. Additional terms for the Apple App Store</h2>
      <p>
        If you downloaded the app from the Apple App Store, Apple's Licensed Application End
        User License Agreement (the standard EULA) governs your licence to the app, and these
        terms apply in addition to it. The following also applies:
      </p>
      <ul>
        <li>
          These terms are an agreement between you and Spiegelei Studio only, not with Apple.
          Spiegelei Studio, not Apple, is solely responsible for the app and its content.
        </li>
        <li>
          Your licence is limited to using the app on Apple devices that you own or control, as
          permitted by the Usage Rules in the Apple Media Services Terms and Conditions.
        </li>
        <li>Apple has no obligation to provide maintenance or support for the app.</li>
        <li>
          If the app fails to conform to any applicable warranty, you may notify Apple, and
          Apple will refund the purchase price, if any. To the maximum extent permitted by law,
          Apple has no other warranty obligation with respect to the app.
        </li>
        <li>
          Spiegelei Studio, not Apple, is responsible for addressing any claims by you or third
          parties relating to the app, including product liability claims, claims that the app
          fails to conform to legal or regulatory requirements, and claims under consumer
          protection or privacy law.
        </li>
        <li>
          If a third party claims that the app infringes its intellectual property rights,
          Spiegelei Studio, not Apple, is responsible for the investigation, defence,
          settlement, and discharge of that claim.
        </li>
        <li>
          You confirm that you are not located in a country subject to a US Government embargo
          or designated by the US Government as a "terrorist supporting" country, and that you
          are not on any US Government list of prohibited or restricted parties.
        </li>
        <li>
          Apple and its subsidiaries are third-party beneficiaries of these terms and, once you
          accept them, have the right to enforce them against you.
        </li>
      </ul>

      <h2>14. Contact</h2>
      <p>
        Questions about these terms can be sent to{' '}
        <a href="mailto:service@spiegeleistudio.com">service@spiegeleistudio.com</a>.
      </p>
    </LegalLayout>
  );
};

export default DracoGermanTerms;
